import { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import { EXCLUDED_CATEGORIES, getGroup, REGISTRY_COLORS } from '../utils/formatters';

const COUNTRY_NAME_MAP = {
  'Congo, The Democratic Republic of The': 'DR Congo',
  'Congo, Republic of': 'Congo',
  'Korea, Republic of': 'South Korea',
  'Tanzania, United Republic of': 'Tanzania',
  "Lao People's Democratic Republic": 'Laos',
  'Bolivia, Plurinational State of': 'Bolivia',
  'Venezuela, Bolivarian Republic of': 'Venezuela',
  'Iran, Islamic Republic of': 'Iran',
  'Viet Nam': 'Vietnam',
  'Russian Federation': 'Russia',
  'Syrian Arab Republic': 'Syria',
  "Côte d'Ivoire": 'Ivory Coast',
  'Moldova, Republic of': 'Moldova',
  'US': 'United States',
  'MX': 'Mexico',
  'DE': 'Germany',
  'FR': 'France',
  'NI': 'Nicaragua',
  'SV': 'El Salvador',
  'Lao': 'Laos',
  'Bolivia Plurinational State of': 'Bolivia',
  "C\u221a\u00a5te d'Ivoire": 'Ivory Coast',
  'Tanzania United Republic of': 'Tanzania',
  'Tanzania, United Republic Of': 'Tanzania',
  "Cote D'Ivoire": 'Ivory Coast',
  'Congo the Democratic Republic of the': 'DR Congo',
};

const useData = (selectedRegistry, selectedYearRange, selectedActivity, selectedGroup) => {
  const [rawAgg, setRawAgg] = useState(null);
  const [rawCountry, setRawCountry] = useState(null);
  const [rawProjCounts, setRawProjCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load CSVs on mount
  useEffect(() => {
    const loadCSV = (url) =>
      new Promise((resolve, reject) => {
        Papa.parse(url, {
          download: true,
          header: true,
          skipEmptyLines: true,
          complete: (r) => resolve(r.data),
          error: (e) => reject(e),
        });
      });

    Promise.all([
      loadCSV(process.env.PUBLIC_URL + '/aggregated_data.csv'),
      loadCSV(process.env.PUBLIC_URL + '/country_aggregated_data.csv'),
      loadCSV(process.env.PUBLIC_URL + '/project_counts.csv'),
    ])
      .then(([agg, country, projCounts]) => {
        const normalizeAgg = agg
          .map((r) => ({
            registry: (r['Registry'] || '').trim(),
            category: (r['Project Type Category'] || '').trim(),
            year: parseFloat(r['Vintage Year']) || 0,
            credits: parseInt(r['Total Credits Issued']) || 0,
            creditsRetired: parseInt(r['total_credits_retired']) || 0,
          }))
          .filter((d) => d.registry && d.category && d.year > 0 && !EXCLUDED_CATEGORIES.includes(d.category));

        const normalizeCountry = country
          .map((r) => ({
            registry: (r['Registry'] || '').trim(),
            country: (() => { const raw = (r['Country'] || '').trim(); return COUNTRY_NAME_MAP[raw] || raw; })(),
            category: (r['Project Type Category'] || '').trim(),
            year: parseFloat(r['Vintage Year']) || 0,
            credits: parseInt(r['Total Credits Issued']) || 0,
            // Per-(Registry, Country, Category) lifetime totals
            creditsRetired:    parseFloat(r['total_credits_retired'])   || 0,
            creditsRemaining:  parseFloat(r['total_credits_remaining']) || 0,
            retirementRate:    parseFloat(r['retirement_rate'])         || 0,
            registryBreakdown: (() => { try { return JSON.parse(r['registry_breakdown'] || 'null'); } catch (e) { return null; } })(),
            vintageCreditsRetired: parseInt(r['vintage_credits_retired'], 10) || 0,
            vintageRetirementRate: parseFloat(r['vintage_retirement_rate']) || 0,
            projectIds: (() => { try { return JSON.parse(r['project_ids'] || '[]'); } catch(e) { return []; } })(),
          }))
          .filter((d) => d.registry && d.country && d.country !== 'International' && d.category && d.year > 0 && !EXCLUDED_CATEGORIES.includes(d.category));

        setRawAgg(normalizeAgg);
        setRawCountry(normalizeCountry);
        setRawProjCounts(projCounts);
        setLoading(false);
      })
      .catch((err) => {
        console.error('CSV load error:', err);
        setError(err);
        setLoading(false);
      });
  }, []);

  const releaseYear = useMemo(() => {
    if (!rawAgg || rawAgg.length === 0) return new Date().getFullYear();
    const currentYear = new Date().getFullYear();
    const years = rawAgg.map(d => d.year).filter(y => y > 0 && y <= currentYear);
    return years.length > 0 ? Math.max(...years) : currentYear;
  }, [rawAgg]);

  const dataMinYear = useMemo(() => {
    if (!rawAgg || rawAgg.length === 0) return 1996;
    const years = rawAgg.map(d => d.year).filter(y => y > 0);
    return years.length > 0 ? Math.min(...years) : 1996;
  }, [rawAgg]);

  // Apply global filters
  const filteredAgg = useMemo(() => {
    if (!rawAgg) return [];
    return rawAgg.filter((d) => {
      if (selectedYearRange) {
        if (d.year < selectedYearRange[0] || (d.year > selectedYearRange[1] && d.year <= releaseYear)) return false;
      }
      if (selectedRegistry && selectedRegistry !== 'all') {
        const regLower = selectedRegistry.toLowerCase();
        if (regLower === 'gold') {
          if (d.registry !== 'Gold Standard') return false;
        } else if (regLower === 'car') {
          if (d.registry !== 'CAR') return false;
        } else {
          if (d.registry.toLowerCase() !== regLower) return false;
        }
      }
      if (selectedGroup && selectedGroup !== 'all') {
        if (getGroup(d.category) !== selectedGroup) return false;
      }
      if (selectedActivity && selectedActivity !== 'all') {
        if (d.category !== selectedActivity) return false;
      }
      return true;
    });
  }, [rawAgg, selectedRegistry, selectedYearRange, releaseYear, selectedGroup, selectedActivity]);

  const filteredCountry = useMemo(() => {
    if (!rawCountry) return [];
    return rawCountry.filter((d) => {
      if (selectedYearRange) {
        if (d.year < selectedYearRange[0] || (d.year > selectedYearRange[1] && d.year <= releaseYear)) return false;
      }
      if (selectedRegistry && selectedRegistry !== 'all') {
        const regLower = selectedRegistry.toLowerCase();
        if (regLower === 'gold') {
          if (d.registry !== 'Gold Standard') return false;
        } else if (regLower === 'car') {
          if (d.registry !== 'CAR') return false;
        } else {
          if (d.registry.toLowerCase() !== regLower) return false;
        }
      }
      if (selectedGroup && selectedGroup !== 'all') {
        if (getGroup(d.category) !== selectedGroup) return false;
      }
      if (selectedActivity && selectedActivity !== 'all') {
        if (d.category !== selectedActivity) return false;
      }
      return true;
    });
  }, [rawCountry, selectedRegistry, selectedYearRange, releaseYear, selectedGroup, selectedActivity]);

  // Computed values
  const totalCredits = useMemo(() => filteredAgg.reduce((s, d) => s + d.credits, 0), [filteredAgg]);

  const creditsByActivity = useMemo(() => {
    const map = {};
    filteredAgg.forEach((d) => {
      if (!map[d.category]) map[d.category] = { credits: 0, registries: {} };
      map[d.category].credits += d.credits;
      if (!map[d.category].registries[d.registry]) map[d.category].registries[d.registry] = 0;
      map[d.category].registries[d.registry] += d.credits;
    });
    return Object.entries(map)
      .map(([name, info]) => ({
        name,
        credits: info.credits,
        group: getGroup(name),
        registryBreakdown: Object.entries(info.registries)
          .map(([rName, rCredits]) => ({ name: rName, credits: rCredits, color: REGISTRY_COLORS[rName] || '#999' }))
          .sort((a, b) => b.credits - a.credits),
      }))
      .sort((a, b) => b.credits - a.credits);
  }, [filteredAgg]);

  const creditsByRegistry = useMemo(() => {
    const map = {};
    filteredAgg.forEach((d) => {
      if (!map[d.registry]) map[d.registry] = 0;
      map[d.registry] += d.credits;
    });
    return Object.entries(map)
      .map(([name, credits]) => ({ name, credits, color: REGISTRY_COLORS[name] || '#999' }))
      .sort((a, b) => b.credits - a.credits);
  }, [filteredAgg]);

  const creditsByCountry = useMemo(() => {
    const map = {};
    filteredCountry.forEach((d) => {
      if (!map[d.country]) map[d.country] = { credits: 0, activities: new Set() };
      map[d.country].credits += d.credits;
      map[d.country].activities.add(d.category);
    });
    return Object.entries(map)
      .map(([name, info]) => ({ name, credits: info.credits, activityCount: info.activities.size }))
      .sort((a, b) => b.credits - a.credits);
  }, [filteredCountry]);

  const creditsByActivityAndRegistry = useMemo(() => {
    // For each activity, registry breakdown
    return creditsByActivity;
  }, [creditsByActivity]);

  const creditsByCountryAndActivity = useMemo(() => {
    const map = {};
    filteredCountry.forEach((d) => {
      const key = d.country;
      if (!map[key]) map[key] = {};
      if (!map[key][d.category]) map[key][d.category] = 0;
      map[key][d.category] += d.credits;
    });
    return map;
  }, [filteredCountry]);

  const allActivities = useMemo(() => {
    const set = new Set(filteredAgg.map((d) => d.category));
    return Array.from(set).sort();
  }, [filteredAgg]);

  const allCountries = useMemo(() => {
    const set = new Set(filteredCountry.map((d) => d.country));
    return Array.from(set).sort();
  }, [filteredCountry]);

  const creditsByGroup = useMemo(() => {
    const map = {};
    creditsByActivity.forEach((a) => {
      if (!map[a.group]) map[a.group] = 0;
      map[a.group] += a.credits;
    });
    return map;
  }, [creditsByActivity]);

  const projectCountByCategory = useMemo(() => {
    if (!rawProjCounts) return {};
    const map = {}; // cat -> Set of project_ids
    rawProjCounts.forEach((row) => {
      const reg = (row['Registry'] || '').trim();
      const cat = (row['Project Type Category'] || '').trim();
      const yr  = parseInt(row['Vintage Year'], 10) || 0;
      if (!cat) return;
      if (EXCLUDED_CATEGORIES.includes(cat)) return;
      if (selectedRegistry && selectedRegistry !== 'all') {
        const regLower = selectedRegistry.toLowerCase();
        if (regLower === 'gold') {
          if (reg !== 'Gold Standard') return;
        } else if (regLower === 'car') {
          if (reg !== 'CAR') return;
        } else {
          if (reg.toLowerCase() !== regLower) return;
        }
      }
      if (selectedYearRange && (yr < selectedYearRange[0] || yr > selectedYearRange[1])) return;
      if (selectedGroup && selectedGroup !== 'all') {
        if (getGroup(cat) !== selectedGroup) return;
      }
      if (selectedActivity && selectedActivity !== 'all') {
        if (cat !== selectedActivity) return;
      }
      if (!map[cat]) map[cat] = new Set();
      try {
        JSON.parse(row['project_ids'] || '[]').forEach(id => map[cat].add(id));
      } catch(e) {
        map[cat].add(`${reg}_${cat}_${yr}`);
      }
    });
    const result = {};
    Object.entries(map).forEach(([cat, idSet]) => { result[cat] = idSet.size; });
    return result;
  }, [rawProjCounts, selectedRegistry, selectedYearRange, selectedGroup, selectedActivity]);

  const totalProjectCount = useMemo(
    () => Object.values(projectCountByCategory).reduce((s, v) => s + v, 0),
    [projectCountByCategory]
  );

  const globalRetirementStats = useMemo(() => {
    if (!rawProjCounts) return { globalRetirementRate: 0, globalCreditsRetired: 0 };
    let totalIssued = 0;
    let totalRetired = 0;
    rawProjCounts.forEach((row) => {
      const reg = (row['Registry'] || '').trim();
      const yr  = parseInt(row['Vintage Year'], 10) || 0;
      if (!reg) return;
      if (selectedRegistry && selectedRegistry !== 'all') {
        const regLower = selectedRegistry.toLowerCase();
        if (regLower === 'gold') { if (reg !== 'Gold Standard') return; }
        else if (regLower === 'car') { if (reg !== 'CAR') return; }
        else { if (reg.toLowerCase() !== regLower) return; }
      }
      if (selectedYearRange && (yr < selectedYearRange[0] || yr > selectedYearRange[1])) return;
      const gCat = (row['Project Type Category'] || '').trim();
      if (selectedGroup && selectedGroup !== 'all') {
        if (getGroup(gCat) !== selectedGroup) return;
      }
      if (selectedActivity && selectedActivity !== 'all') {
        if (gCat !== selectedActivity) return;
      }
      totalIssued  += parseInt(row['total_credits_issued'],  10) || 0;
      totalRetired += parseInt(row['total_credits_retired'], 10) || 0;
    });
    const globalRetirementRate = totalIssued > 0
      ? Math.round(totalRetired / totalIssued * 1000) / 10
      : 0;
    return { globalRetirementRate, globalCreditsRetired: totalRetired };
  }, [rawProjCounts, selectedRegistry, selectedYearRange, selectedGroup, selectedActivity]);

  const registryStats = useMemo(() => {
    if (!rawProjCounts) return {};
    const accum = {};
    rawProjCounts.forEach((row) => {
      const reg = (row['Registry'] || '').trim();
      const yr  = parseInt(row['Vintage Year'], 10) || 0;
      if (!reg) return;
      if (selectedRegistry && selectedRegistry !== 'all') {
        const regLower = selectedRegistry.toLowerCase();
        if (regLower === 'gold') { if (reg !== 'Gold Standard') return; }
        else if (regLower === 'car') { if (reg !== 'CAR') return; }
        else { if (reg.toLowerCase() !== regLower) return; }
      }
      if (selectedYearRange && (yr < selectedYearRange[0] || yr > selectedYearRange[1])) return;
      const rCat = (row['Project Type Category'] || '').trim();
      if (selectedGroup && selectedGroup !== 'all') {
        if (getGroup(rCat) !== selectedGroup) return;
      }
      if (selectedActivity && selectedActivity !== 'all') {
        if (rCat !== selectedActivity) return;
      }
      if (!accum[reg]) accum[reg] = { issued: 0, retired: 0, remaining: 0, idSet: new Set() };
      accum[reg].issued    += parseInt(row['total_credits_issued'],    10) || 0;
      accum[reg].retired   += parseInt(row['total_credits_retired'],   10) || 0;
      accum[reg].remaining += parseInt(row['total_credits_remaining'], 10) || 0;
      try { JSON.parse(row['project_ids'] || '[]').forEach(id => accum[reg].idSet.add(id)); } catch(e) {}
    });
    const map = {};
    Object.entries(accum).forEach(([reg, a]) => {
      map[reg] = {
        issued:         a.issued,
        retired:        a.retired,
        remaining:      a.remaining,
        retirementRate: a.issued > 0 ? Math.round(a.retired / a.issued * 1000) / 10 : 0,
        projectCount:   a.idSet.size,
      };
    });
    return map;
  }, [rawProjCounts, selectedRegistry, selectedYearRange, selectedGroup, selectedActivity]);

  // Country-specific data for country explorer
  const getCountryData = (countryName) => {
    if (!countryName || countryName === '🌍 Global') {
      return {
        records: filteredCountry,
        isGlobal: true,
      };
    }
    return {
      records: filteredCountry.filter((d) => d.country === countryName),
      isGlobal: false,
    };
  };

  // Activity-specific country data
  const getActivityCountries = (activityName) => {
    if (!activityName) return [];
    const map = {};
    filteredCountry.forEach((d) => {
      if (d.category === activityName) {
        if (!map[d.country]) map[d.country] = 0;
        map[d.country] += d.credits;
      }
    });
    return Object.entries(map)
      .map(([name, credits]) => ({ name, credits }))
      .sort((a, b) => b.credits - a.credits);
  };

  // Trend data for an activity
  const getActivityTrend = (activityName) => {
    const map = {};
    filteredAgg.forEach((d) => {
      if (d.category === activityName) {
        const yr = Math.floor(d.year);
        if (!map[yr]) map[yr] = 0;
        map[yr] += d.credits;
      }
    });
    return Object.entries(map)
      .map(([year, credits]) => ({ year: parseInt(year), credits }))
      .sort((a, b) => a.year - b.year);
  };

  return {
    loading,
    error,
    dataMinYear,
    releaseYear,
    totalCredits,
    creditsByActivity,
    creditsByRegistry,
    creditsByCountry,
    creditsByActivityAndRegistry,
    creditsByCountryAndActivity,
    allActivities,
    allCountries,
    creditsByGroup,
    filteredAgg,
    filteredCountry,
    getCountryData,
    getActivityCountries,
    getActivityTrend,
    projectCountByCategory,
    totalProjectCount,
    globalRetirementRate: globalRetirementStats.globalRetirementRate,
    globalCreditsRetired: globalRetirementStats.globalCreditsRetired,
    registryStats,
  };
};

export default useData;
